import React, { useState, useEffect } from 'react';
import Input from '../common/Input';
import TextArea from '../common/TextArea';
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../../lib/config/api-endpoints';
// import { useAuth } from '../../lib/auth';

interface ContentItem {
  id: number;
  title: string;
  description: string;
  pdf_file_path: string;
  file_size?: number;
  price_cents: number;
  currency: string;
  tags?: string;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ContentFormData {
  title: string;
  description: string;
  price: number;
  tags: string;
  file: File | null;
  sort_order: number;
}

export const AdminContentManager: React.FC = () => {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState<ContentFormData>({
    title: '',
    description: '',
    price: 0,
    tags: '',
    file: null,
    sort_order: 0
  });

  useEffect(() => {
    fetchContentItems();
  }, []);

  const fetchContentItems = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.admin.content, {
        headers: getAuthHeaders(API_ENDPOINTS.admin.content),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.content) ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch content items');
      }

      const data = await response.json();
      setContentItems(data.success ? data.data.content_items : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, file }));
      setError(null);
    }
  };

  const handleInputChange = (field: keyof ContentFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.file) {
      setError('Title and PDF file are required');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const submitFormData = new FormData();
      
      submitFormData.append('title', formData.title);
      submitFormData.append('description', formData.description);
      submitFormData.append('price', (formData.price * 100).toString()); // Convert to cents
      submitFormData.append('tags', formData.tags);
      submitFormData.append('sort_order', formData.sort_order.toString());
      submitFormData.append('file', formData.file);

      const response = await fetch(API_ENDPOINTS.admin.content, {
        method: 'POST',
        headers: getAuthHeaders(true, false), // Include auth but not Content-Type for FormData
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.content) ? 'include' : 'same-origin',
        body: submitFormData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload content');
      }

      // Reset form and refresh list
      setFormData({
        title: '',
        description: '',
        price: 0,
        tags: '',
        file: null,
        sort_order: 0
      });
      setShowUploadForm(false);
      fetchContentItems();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item: ContentItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      price: item.price_cents / 100, // Convert from cents
      tags: item.tags || '',
      file: null,
      sort_order: item.sort_order
    });
    setShowUploadForm(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingItem) return;

    setUploading(true);
    setError(null);

    try {
      const updateData = {
        title: formData.title,
        description: formData.description,
        price: formData.price * 100, // Convert to cents
        tags: formData.tags,
        sort_order: formData.sort_order
      };

      const response = await fetch(`${API_ENDPOINTS.admin.content}/${editingItem.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(`${API_ENDPOINTS.admin.content}/${editingItem.id}`),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.content) ? 'include' : 'same-origin',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update content');
      }

      // Reset form and refresh list
      setFormData({
        title: '',
        description: '',
        price: 0,
        tags: '',
        file: null,
        sort_order: 0
      });
      setEditingItem(null);
      setShowUploadForm(false);
      fetchContentItems();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item: ContentItem) => {
    if (!confirm(`Are you sure you want to delete "${item.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.admin.content}/${item.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(`${API_ENDPOINTS.admin.content}/${item.id}`),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.admin.content) ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete content');
      }

      fetchContentItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const formatFileSize = (sizeInBytes?: number) => {
    if (!sizeInBytes) return 'Unknown';
    const mb = sizeInBytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading content...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Content Management</h2>
        <button
          onClick={() => {
            setShowUploadForm(!showUploadForm);
            setEditingItem(null);
            setFormData({
              title: '',
              description: '',
              price: 0,
              tags: '',
              file: null,
              sort_order: 0
            });
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {showUploadForm ? 'Cancel' : 'Upload New Content'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {showUploadForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? 'Edit Content' : 'Upload New PDF Content'}
          </h3>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-900 mb-2">📄 PDF Download System</h4>
            <p className="text-blue-700 text-sm mb-3">
              Upload high-quality PDF guides that customers can download and read offline. 
              Perfect for wellness guides, meal plans, and educational resources.
            </p>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Customers get instant PDF download after purchase</li>
              <li>• Files are securely stored and access-controlled</li>
              <li>• Lifetime access for customers to re-download</li>
              <li>• Mobile-friendly and print-ready format</li>
            </ul>
          </div>
          
          <form onSubmit={editingItem ? handleUpdate : handleSubmit} className="space-y-4">
            <Input
              label="Title"
              id="content-title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
            />

            <TextArea
              label="Description"
              id="content-description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Price (USD)"
                id="content-price"
                type="number"
                value={formData.price.toString()}
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                required
              />

              <Input
                label="Sort Order"
                id="content-sort-order"
                type="number"
                value={formData.sort_order.toString()}
                onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
              />
            </div>

            <Input
              label="Tags (comma-separated)"
              id="content-tags"
              value={formData.tags}
              onChange={(e) => handleInputChange('tags', e.target.value)}
              placeholder="nutrition, guide, beginner"
            />

            {!editingItem && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF File
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  PDF files only, maximum 10MB
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={uploading}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {uploading ? 'Processing...' : (editingItem ? 'Update Content' : 'Upload Content')}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowUploadForm(false);
                  setEditingItem(null);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Existing Content ({contentItems.length})</h3>
        </div>
        
        {contentItems.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No content uploaded yet. Upload your first content item to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {contentItems.map((item) => (
              <div key={item.id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{item.title}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.is_published 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.is_published ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span>Price: {formatPrice(item.price_cents)}</span>
                      <span>Size: {formatFileSize(item.file_size)}</span>
                      <span>Order: {item.sort_order}</span>
                      <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                    
                    {item.tags && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1">
                          {JSON.parse(item.tags).map((tag: string, index: number) => (
                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(item)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                    >
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleDelete(item)}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};